/**
 * @fileoverview Input/output schemas for world-object-details-generator-v3.
 *
 * This agent generates complete details for a world object based on a selected direction.
 * Supports multimodal input with optional reference images.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { WorldObjectAttributesV3Schema } from '@/domains/world-building/schemas';
import { WorldObjectDetailsIdeaSchema } from '../world-object-details-ideation-v3/schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Supports optional reference images for multimodal generation.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeWorldObject.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  selectedIdea: WorldObjectDetailsIdeaSchema.describe('The creative direction selected by the user.'),
  /**
   * Optional array of image data URIs (base64 encoded).
   * Each should be in format: data:image/jpeg;base64,... or data:image/png;base64,...
   * Maximum 5 images allowed.
   */
  imageDataUris: z.array(z.string()).max(5).optional().describe(
    "Optional array of image data URIs (base64 encoded) as visual references for the object."
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema includes validation metadata and object attributes.
 * The hook will wrap this in snapshot metadata (id, chapterId, etc.).
 */
export const OutputSchema = z.object({
  /**
   * Whether the image validation passed (if images were provided).
   * False if the image appears to be just text or an invalid reference.
   * Always true if no images were provided.
   */
  isValidImage: z.boolean().describe(
    "True if no images were provided OR if images are valid visual references. False if image validation failed."
  ),
  /**
   * Error message if validation failed.
   */
  validationError: z.string().optional().describe(
    "Explanation of why the image is invalid, if isValidImage is false."
  ),
  /**
   * The generated object attributes. Only present if isValidImage is true.
   */
  attributes: WorldObjectAttributesV3Schema.optional().describe(
    "The generated object attributes. Only present if isValidImage is true."
  ),
});

export type Output = z.infer<typeof OutputSchema>;
